import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "./docUtils";

const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
const emptyItem = () => ({ prestation: "", description: "", qty: 1, price_ht: "" });
const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white";

export default function QuoteCreateModal({ open, onClose, onSaved, clients, quote = null, presetClientId = null }) {
  const isEdit = !!quote;
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [tva, setTva] = useState("non_assujetti");
  const [delay, setDelay] = useState("");
  const [comment, setComment] = useState("");
  const [discount, setDiscount] = useState("");
  const [validity, setValidity] = useState(30);
  const [acompteType, setAcompteType] = useState("aucun");
  const [acompteValue, setAcompteValue] = useState("");
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createdClients, setCreatedClients] = useState([]);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [nc, setNc] = useState({ raison_sociale: "", email: "", phone: "", address: "", zip_code: "", city: "", siret: "" });
  const [ncDups, setNcDups] = useState(null);
  const [ncBusy, setNcBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (quote) {
      setClientId(quote.client_id || "");
      setItems(quote.items?.length ? quote.items.map((i) => ({ ...i })) : [emptyItem()]);
      setTva(quote.tva_mode === "non_assujetti" ? "non_assujetti" : "tva");
      setDelay(quote.delay || "");
      setComment(quote.comment || "");
      setDiscount(quote.discount_percent || "");
      setValidity(quote.validity_days || 30);
      setAcompteType(quote.acompte_type || "aucun");
      setAcompteValue(quote.acompte_value || "");
    } else {
      setClientId(presetClientId || ""); setItems([emptyItem()]); setTva("non_assujetti");
      setDelay(""); setComment(""); setDiscount(""); setValidity(30);
      setAcompteType("aucun"); setAcompteValue("");
    }
    setNewClientOpen(false);
    setNcDups(null);
    setCreatedClients([]);
    api.get("/accounting/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/accounting/services").then((r) => setServices(r.data)).catch(() => {});
  }, [open, quote]);

  const setItem = (i, k, v) => setItems((its) => its.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const totalBrut = items.reduce((s, it) => s + num(it.qty || 1) * num(it.price_ht), 0);
  const discountPct = Math.min(100, Math.max(0, num(discount)));
  const discountAmt = Math.round(totalBrut * discountPct) / 100;
  const totalNet = totalBrut - discountAmt;
  const totalTtc = tva === "non_assujetti" ? totalNet : totalNet * 1.2;

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setItems(t.items.map((i) => ({ ...i })));
    setTva(t.tva_mode === "non_assujetti" ? "non_assujetti" : "tva");
    setDelay(t.delay || ""); setComment(t.comment || "");
  };

  const pickService = (sid) => {
    const s = services.find((x) => x.id === sid);
    if (!s) return;
    const row = { prestation: s.name + (s.unit === "mois" ? " (mensuel)" : ""), description: s.description || "", qty: 1, price_ht: s.price_ht };
    setItems((its) => (its.length === 1 && !its[0].prestation ? [row] : [...its, row]));
  };

  const createClient = async (force = false) => {
    if (!nc.raison_sociale.trim()) { toast.error("Nom / raison sociale requis"); return; }
    setNcBusy(true);
    try {
      const r = await api.post("/clients/quick-create", { ...nc, force });
      if (r.data.duplicates) { setNcDups(r.data.duplicates); return; }
      const c = r.data.client;
      setCreatedClients((cs) => [...cs, c]);
      setClientId(c.id);
      setNewClientOpen(false);
      setNc({ raison_sociale: "", email: "", phone: "", address: "", zip_code: "", city: "", siret: "" });
      setNcDups(null);
      toast.success(`Client « ${c.name} » créé — ses coordonnées figureront sur le devis`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setNcBusy(false); }
  };

  const submit = async () => {
    if (!clientId && !isEdit) { toast.error("Sélectionnez un client"); return; }
    const clean = items.filter((it) => it.prestation.trim());
    if (!clean.length) { toast.error("Ajoutez au moins une prestation"); return; }
    setLoading(true);
    const payload = {
      items: clean.map((it) => ({ ...it, qty: num(it.qty || 1), price_ht: num(it.price_ht) })),
      tva_mode: tva, tva_rate: tva === "non_assujetti" ? 0 : 20,
      delay, comment, acompte_type: acompteType, acompte_value: num(acompteValue),
      discount_percent: discountPct, validity_days: num(validity) || 30,
    };
    try {
      if (isEdit) {
        await api.patch(`/accounting/quotes/${quote.id}`, payload);
        toast.success("Devis mis à jour — la version client est actualisée automatiquement");
      } else {
        await api.post("/accounting/quotes", { ...payload, client_id: clientId });
        toast.success("Devis créé en brouillon — son numéro sera attribué à l'envoi");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const acompteAmount = acompteType === "pourcent" ? totalTtc * num(acompteValue) / 100 : num(acompteValue);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-create-modal" aria-describedby={undefined}>
        <DialogTitle className="font-heading font-bold text-lg">
          {isEdit ? `Modifier le devis ${quote?.number ? `N° ${quote.number}` : ""}` : "Nouveau devis"}
        </DialogTitle>
        <div className="space-y-4 mt-2">
          {!isEdit && templates.length > 0 && (
            <div>
              <Label className="text-xs">À partir d'un modèle</Label>
              <select className={`${sel} mt-1`} value="" onChange={(e) => applyTemplate(e.target.value)} data-testid="quote-create-template">
                <option value="">Choisir un modèle…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Client</Label>
              {!isEdit && (
                <button type="button" onClick={() => { setNewClientOpen((v) => !v); setNcDups(null); }}
                  className="text-xs text-neutral-500 hover:text-neutral-900 flex items-center gap-1 transition-colors"
                  data-testid="quote-new-client-toggle">
                  <Plus className="w-3 h-3" /> Nouveau client
                </button>
              )}
            </div>
            <select className={`${sel} mt-1`} value={clientId} onChange={(e) => setClientId(e.target.value)}
              disabled={isEdit && !quote?.client_id} data-testid="quote-create-client">
              <option value="">Sélectionner…</option>
              {[...createdClients, ...clients].map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {isEdit && !quote?.client_id && <p className="text-[11px] text-neutral-400 mt-1">Devis lié à un prospect : le client ne peut pas être changé ici.</p>}
            {newClientOpen && !isEdit && (
              <div className="mt-2 border border-neutral-200 rounded-lg p-3 space-y-2 bg-neutral-50" data-testid="quote-new-client-form">
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input placeholder="Raison sociale / Nom *" value={nc.raison_sociale} onChange={(e) => setNc({ ...nc, raison_sociale: e.target.value })} data-testid="nc-name" />
                  <Input placeholder="Email (envoi du devis)" type="email" value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} data-testid="nc-email" />
                  <Input placeholder="Téléphone" value={nc.phone} onChange={(e) => setNc({ ...nc, phone: e.target.value })} data-testid="nc-phone" />
                  <Input placeholder="SIRET (optionnel)" value={nc.siret} onChange={(e) => setNc({ ...nc, siret: e.target.value })} data-testid="nc-siret" />
                </div>
                <Input placeholder="Adresse" value={nc.address} onChange={(e) => setNc({ ...nc, address: e.target.value })} data-testid="nc-address" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Code postal" value={nc.zip_code} onChange={(e) => setNc({ ...nc, zip_code: e.target.value })} data-testid="nc-zip" />
                  <Input placeholder="Ville" value={nc.city} onChange={(e) => setNc({ ...nc, city: e.target.value })} data-testid="nc-city" />
                </div>
                <p className="text-[11px] text-neutral-400">Ces coordonnées apparaîtront dans le bloc « Adressé à » du devis (nom, adresse, email, SIRET).</p>
                {ncDups && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2.5 text-xs space-y-1.5" data-testid="nc-duplicates">
                    <p className="font-medium text-yellow-800">Client(s) similaire(s) existant(s) :</p>
                    {ncDups.map((d) => (
                      <button key={d.id} type="button"
                        onClick={() => { setClientId(d.id); setNewClientOpen(false); setNcDups(null); toast.success(`Client « ${d.name} » sélectionné`); }}
                        className="block text-left text-blue-700 hover:underline" data-testid={`nc-dup-${d.id}`}>
                        Utiliser « {d.name} »{d.email ? ` (${d.email})` : ""}
                      </button>
                    ))}
                    <button type="button" onClick={() => createClient(true)} className="text-yellow-800 underline" data-testid="nc-force">Créer quand même</button>
                  </div>
                )}
                <Button size="sm" onClick={() => createClient(false)} disabled={ncBusy}
                  className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="nc-create-btn">
                  {ncBusy ? "Création…" : "Créer et sélectionner ce client"}
                </Button>
              </div>
            )}
          </div>
          {services.length > 0 && (
            <div>
              <Label className="text-xs">Bibliothèque de prestations</Label>
              <select className={`${sel} mt-1`} value="" onChange={(e) => pickService(e.target.value)} data-testid="quote-create-service">
                <option value="">Ajouter une prestation enregistrée…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {eur(s.price_ht)}{s.unit === "mois" ? " / mois" : ""}</option>)}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs">Prestations</Label>
            {items.map((it, i) => (
              <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid={`quote-item-${i}`}>
                <div className="flex gap-2 items-start">
                  <Textarea placeholder={"Prestation / Désignation\n(retours à la ligne acceptés)"} value={it.prestation}
                    onChange={(e) => setItem(i, "prestation", e.target.value)} rows={2}
                    className="flex-1 text-sm" data-testid={`quote-prestation-${i}`} />
                  {items.length > 1 && (
                    <button onClick={() => setItems((its) => its.filter((_, j) => j !== i))} className="text-neutral-300 hover:text-red-500 mt-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Textarea placeholder="Description détaillée (optionnel)" value={it.description}
                  onChange={(e) => setItem(i, "description", e.target.value)} rows={2}
                  className="text-sm" data-testid={`quote-description-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Quantité</Label>
                    <Input type="number" min="0.5" step="0.5" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="mt-1" /></div>
                  <div><Label className="text-xs">Prix unitaire HT (€)</Label>
                    <Input type="number" min="0" value={it.price_ht} onChange={(e) => setItem(i, "price_ht", e.target.value)} className="mt-1" data-testid={`quote-price-${i}`} /></div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setItems((its) => [...its, emptyItem()])} className="gap-1.5" data-testid="quote-add-item">
              <Plus className="w-4 h-4" /> Ajouter une prestation
            </Button>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">TVA</Label>
              <select className={`${sel} mt-1`} value={tva} onChange={(e) => setTva(e.target.value)} data-testid="quote-create-tva">
                <option value="non_assujetti">Non assujetti (art. 293 B du CGI)</option>
                <option value="tva">TVA 20 %</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Délai d'exécution</Label>
              <Input value={delay} onChange={(e) => setDelay(e.target.value)} placeholder="Ex : 3 semaines" className="mt-1" data-testid="quote-create-delay" />
            </div>
            <div>
              <Label className="text-xs">Validité du devis (jours)</Label>
              <Input type="number" min="1" max="365" value={validity} onChange={(e) => setValidity(e.target.value)} className="mt-1" data-testid="quote-create-validity" />
            </div>
          </div>

          <div className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid="quote-discount-block">
            <Label className="text-xs">Remise (%)</Label>
            <Input type="number" min="0" max="100" step="0.5" value={discount} onChange={(e) => setDiscount(e.target.value)}
              placeholder="0" className="mt-1" data-testid="quote-create-discount" />
            {discountPct > 0 && (
              <div className="text-xs bg-neutral-50 rounded-lg p-2.5 space-y-1" data-testid="quote-discount-preview">
                <div className="flex justify-between"><span className="text-neutral-500">Montant initial</span><span>{eur(totalBrut)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Remise ({discountPct} %)</span><span className="text-red-600">− {eur(discountAmt)}</span></div>
                <div className="flex justify-between font-medium"><span>Total après remise</span><span>{eur(totalNet)}</span></div>
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Acompte demandé</Label>
              <select className={`${sel} mt-1`} value={acompteType} onChange={(e) => setAcompteType(e.target.value)} data-testid="quote-create-acompte-type">
                <option value="aucun">Aucun</option>
                <option value="pourcent">Pourcentage</option>
                <option value="montant">Montant fixe</option>
              </select>
            </div>
            {acompteType !== "aucun" && (
              <div>
                <Label className="text-xs">{acompteType === "pourcent" ? "Pourcentage (%)" : "Montant (€)"}</Label>
                <Input type="number" min="0" value={acompteValue} onChange={(e) => setAcompteValue(e.target.value)} className="mt-1" data-testid="quote-create-acompte-value" />
              </div>
            )}
          </div>
          {acompteType !== "aucun" && acompteAmount > 0 && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="quote-create-acompte-preview">
              Acompte : <strong>{eur(acompteAmount)}</strong> (calculé sur le total après remise) — solde restant : <strong>{eur(Math.max(0, totalTtc - acompteAmount))}</strong>
            </p>
          )}

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <Input value={comment} onChange={(e) => setComment(e.target.value)} className="mt-1" data-testid="quote-create-comment" />
          </div>

          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm flex justify-between" data-testid="quote-create-total">
            <span className="font-medium">Total {tva === "non_assujetti" ? "" : "TTC"}{discountPct > 0 ? " après remise" : ""}</span>
            <span className="font-bold">{eur(totalTtc)}</span>
          </div>

          <Button onClick={submit} disabled={loading} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="quote-create-save">
            {loading ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer le devis (brouillon)"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
